import { Button, Col, Divider, Input, Modal, Row, Select, Space, Spin, Switch } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { getParametre, saveDecaissements } from "../../../../hooks/useHooks";
import { toast } from "react-toastify";
import { PARAMETERS } from "../../../../variables/constants";
import AmountInputNumber from "../../../../components/inputs/AmountInputNumber";
import { formatAmount } from "../../../../variables/numbersFunctions";

const { TextArea } = Input;

const ALL_BENEFICIARY_CATEGORIES = ["victimes", "experts", "accessoires"];

const REGLEMENT_NATURE_CATEGORY_MAP = {
    "SINISTRE EN PRINCIPAL": ["victimes"],
    ACCESSOIRES: ["victimes", "accessoires"],
    HONORAIRES: ["experts"],
    PROVISION: ["victimes"],
};

const normalizeOptionValue = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    const normalized = `${value}`.trim();
    return normalized.length ? normalized : null;
};

const dedupeOptionList = (options = []) => {
    const seen = new Set();
    return options.filter((option) => {
        const val = option?.value;
        if (!val) {
            return false;
        }
        if (seen.has(val)) {
            return false;
        }
        seen.add(val);
        return true;
    });
};

const buildAccessoireLabel = (item = {}) => {
    const candidates = [
        item?.libelle,
        item?.libelleParametre,
        item?.nom && item?.prenom ? `${item.nom} ${item.prenom}` : undefined,
        item?.nom,
        item?.intitule,
        item?.designation,
        item?.raisonSociale,
        item?.label,
        item?.personne?.raisonSociale,
        item?.personne?.nom,
    ];
    const label = candidates.find((candidate) => typeof candidate === "string" && candidate.trim().length);
    if (label) {
        return label.trim();
    }
    const fallbackValue = normalizeOptionValue(
        item?.codeId || item?.id || item?.value || item?.libelle || item?.libelleParametre
    );
    return fallbackValue ? `Accessoire ${fallbackValue}` : "Accessoire";
};

const buildNatureActeOption = (item = {}) => {
    const candidates = [
        item?.libelleParametre,
        item?.valeur,
        item?.label,
        item?.libelle,
        item?.designation,
        item?.nom,
    ];
    const label = candidates.find((candidate) => typeof candidate === "string" && candidate.trim().length);
    if (!label) {
        return null;
    }
    const normalized = label.trim();
    return {
        label: normalized,
        value: normalized,
    };
};

const formatRepresentantName = (representant) => {
    if (!representant) {
        return null;
    }
    const parts = [representant?.nom, representant?.prenom].filter((part) => typeof part === "string" && part.trim().length);
    return parts.length ? parts.join(" ").trim() : null;
};

const ReglementModal = ({ sinistre, modalControl, sap, sapMin, handleClotureDossier }) => {
    const victimes = sinistre?.victimes || [];
    const experts = sinistre?.experts || [];
    const [selectedBeneficiaireId, setSelectedBeneficiaireId] = useState("");
    const [sousEvaluationsSelected, setSousEvaluationsSelected] = useState([]);
    const [natureReglement, setNatureReglement] = useState([]);
    const [montantNature, setMontantNature] = useState([]);
    const [montantReglement, setMontantReglement] = useState(0)
    const [beneficiaire, setBeneficiaire] = useState(null);
    const [selectedBeneficiaryOption, setSelectedBeneficiaryOption] = useState(null);
    const [honoraire, setHonoraire] = useState(0);
    const [decaissementLoading, setDecaissementLoading] = useState(false)
    const [dossierCloture, setDossierCloture] = useState(false);
    const [motifsLoading, setMotifsLoading] = useState(false);
    const [motifs, setMotifs] = useState([]);
    const [name, setName] = useState('');
    const [motifReglement, setMotifReglement] = useState();
    const [textValue, setTextValue] = useState('');
    const [natureActeOptions, setNatureActeOptions] = useState([]);
    const [natureActeLoading, setNatureActeLoading] = useState(false);
    const [natureActe, setNatureActe] = useState();
    const [formules, setFormules] = useState([]);
    const [formulesLoading, setFormulesLoading] = useState(false);
    const [formuleCloture, setFormuleCloture] = useState();   // valeur sélectionnée dans le Select
    const [formuleClotureText, setFormuleClotureText] = useState(''); // texte éditable final
    const [formuleNewName, setFormuleNewName] = useState('');
    const formuleInputRef = useRef(null);

    const accessoiresBeneficiaires = useMemo(() => {
        const candidateKeys = [
            "accessoiresBeneficiaires",
            "beneficiairesAccessoires",
            "fraisAccessoiresBeneficiaires",
            "fraisAccessoires",
            "accessoires",
        ];
        const aggregated = [];
        candidateKeys.forEach((key) => {
            const items = sinistre?.[key];
            if (Array.isArray(items)) {
                aggregated.push(...items.filter(Boolean));
            }
        });
        if (!aggregated.length && Array.isArray(sinistre?.accessoire)) {
            aggregated.push(...sinistre.accessoire.filter(Boolean));
        }
        return aggregated.filter(Boolean);
    }, [sinistre]);

    const victimesOptions = useMemo(() => {
        const options = (victimes || [])
            .filter((victime) => normalizeOptionValue(victime?.codeId) && victime?.personne)
            .map((victime) => {
                const value = normalizeOptionValue(victime.codeId);
                if (!value) {
                    return null;
                }
                const personne = victime?.personne || {};
                const labelCandidates = [
                    personne?.nom && personne?.prenom ? `${personne.nom} ${personne.prenom}` : undefined,
                    personne?.raisonSociale,
                    personne?.nom,
                    personne?.prenom,
                ];
                const label =
                    labelCandidates.find((candidate) => typeof candidate === "string" && candidate.trim().length) || "Victime";

                return {
                    label: label.trim(),
                    value,
                    beneficiary: { type: "victimes", item: victime },
                };
            })
            .filter(Boolean);
        return dedupeOptionList(options);
    }, [victimes]);

    const expertsOptions = useMemo(() => {
        const options = (experts || [])
            .filter((expert) => normalizeOptionValue(expert?.codeId))
            .map((expert) => {
                const value = normalizeOptionValue(expert.codeId);
                if (!value) {
                    return null;
                }
                const labelCandidates = [
                    expert?.nom && expert?.prenom ? `${expert.nom} ${expert.prenom}` : undefined,
                    expert?.raisonSociale,
                    expert?.nom,
                    expert?.prenom,
                ];
                const label =
                    labelCandidates.find((candidate) => typeof candidate === "string" && candidate.trim().length) || `Expert ${value}`;

                return {
                    label: label.trim(),
                    value,
                    beneficiary: { type: "experts", item: expert },
                };
            })
            .filter(Boolean);
        return dedupeOptionList(options);
    }, [experts]);

    const accessoiresOptions = useMemo(() => {
        const options = (accessoiresBeneficiaires || [])
            .map((accessoire) => {
                const possibleIds = [
                    accessoire?.codeId,
                    accessoire?.id,
                    accessoire?.value,
                    accessoire?.libelle,
                    accessoire?.libelleParametre,
                ];
                const rawValue = possibleIds.map((id) => normalizeOptionValue(id)).find((val) => val);
                if (!rawValue) {
                    return null;
                }
                return {
                    label: buildAccessoireLabel(accessoire),
                    value: rawValue,
                    beneficiary: { type: "accessoires", item: accessoire },
                };
            })
            .filter(Boolean);
        return dedupeOptionList(options);
    }, [accessoiresBeneficiaires]);

    const beneficiaryOptionLookup = useMemo(() => {
        const map = new Map();
        [...victimesOptions, ...expertsOptions, ...accessoiresOptions].forEach((option) => {
            if (option?.value) {
                map.set(option.value, option?.beneficiary?.item);
            }
        });
        return map;
    }, [victimesOptions, expertsOptions, accessoiresOptions]);

    const allowedBeneficiaryCategories = useMemo(() => {
        if (!natureReglement?.length) {
            return ALL_BENEFICIARY_CATEGORIES;
        }
        const intersection = natureReglement.reduce((acc, nature) => {
            const allowedForNature = REGLEMENT_NATURE_CATEGORY_MAP[nature] || ALL_BENEFICIARY_CATEGORIES;
            if (!acc) {
                return [...allowedForNature];
            }
            return acc.filter((category) => allowedForNature.includes(category));
        }, null);
        return Array.isArray(intersection) ? intersection : [];
    }, [natureReglement]);

    const beneficiaryOptionGroups = useMemo(() => {
        const groups = [];
        if (allowedBeneficiaryCategories.includes("victimes") && victimesOptions.length) {
            groups.push({
                label: <span>Victime(s)</span>,
                title: "Victime(s)",
                options: victimesOptions,
            });
        }
        if (allowedBeneficiaryCategories.includes("experts") && expertsOptions.length) {
            groups.push({
                label: <span>Expert(s)</span>,
                title: "Expert(s)",
                options: expertsOptions,
            });
        }
        if (allowedBeneficiaryCategories.includes("accessoires") && accessoiresOptions.length) {
            groups.push({
                label: <span>Accessoire(s)</span>,
                title: "Accessoire(s)",
                options: accessoiresOptions,
            });
        }
        return groups;
    }, [allowedBeneficiaryCategories, victimesOptions, expertsOptions, accessoiresOptions]);

    useEffect(() => {
        if (!selectedBeneficiaireId) {
            return;
        }
        const availableValues = beneficiaryOptionGroups.reduce((acc, group) => {
            if (Array.isArray(group?.options)) {
                group.options.forEach((option) => {
                    if (option?.value) {
                        acc.push(option.value);
                    }
                });
            }
            return acc;
        }, []);
        if (!availableValues.includes(selectedBeneficiaireId)) {
            setSelectedBeneficiaireId("");
            setBeneficiaire(null);
            setSelectedBeneficiaryOption(null);
        }
    }, [beneficiaryOptionGroups, selectedBeneficiaireId]);

    useEffect(() => {
        if (!modalControl?.open) {
            setSelectedBeneficiaireId("");
            setBeneficiaire(null);
            setSelectedBeneficiaryOption(null);
            setNatureActe(undefined);
            return;
        }

        setSelectedBeneficiaireId("");
        setBeneficiaire(null);
        setSelectedBeneficiaryOption(null);
        setSousEvaluationsSelected([]);
        setNatureReglement([]);
        setMontantNature([]);
        setMontantReglement(0);
        setHonoraire(0);
        setMotifReglement(undefined);
        setTextValue('');
        setName('');
        setNatureActe(undefined);
        setFormuleCloture(undefined);
        setFormuleClotureText('');
        setFormuleNewName('');
    }, [modalControl?.open]);

    const beneficiarySelectDisabled = beneficiaryOptionGroups.length === 0;

    const inputRef = useRef(null);

    useEffect(() => {
        if (parseFloat(montantReglement) === parseFloat(sap) - parseFloat(sapMin)) {
            setDossierCloture(true);
        } else {
            setDossierCloture(false);
        }
    }, [montantReglement, sap, sapMin]);

    useEffect(() => {
        const fetchMotifs = async () => {
            setMotifsLoading(true);
            const response = await getParametre(PARAMETERS.motifReglement);
            // console.log("motifs: ", response.data);
            setMotifs(response.data);
            setMotifsLoading(false);
        };
        fetchMotifs()
    }, []);

    useEffect(() => {
        const fetchFormules = async () => {
            setFormulesLoading(true);
            try {
                const response = await getParametre(PARAMETERS.formuleCloture);
                setFormules(response.data || []);
            } catch (e) {
                setFormules([]);
            } finally {
                setFormulesLoading(false);
            }
        };
        fetchFormules();
    }, []);

    useEffect(() => {
        const fetchNatureActes = async () => {
            try {
                setNatureActeLoading(true);
                const response = await getParametre(PARAMETERS.natureActe);
                const options = dedupeOptionList(
                    (response?.data || [])
                        .map((item) => buildNatureActeOption(item))
                        .filter(Boolean)
                );
                setNatureActeOptions(options);
            } catch (error) {
                setNatureActeOptions([]);
            } finally {
                setNatureActeLoading(false);
            }
        };

        fetchNatureActes();
    }, []);

    // Called when a motif is selected
    const onMotifSelect = (value) => {
        handleFindMotif(value); // This should eventually call setTextValue with content
        const selectedOption = motifs.find(opt => opt.valeur === value);
        setTextValue(selectedOption?.valeur || '');
    };


    const handleFindMotif = (e) => {
        setMotifReglement(e)
    }

    const onFormuleSelect = (value) => {
        setFormuleCloture(value);
        const selected = formules.find(opt => opt.valeur === value);
        setFormuleClotureText(selected?.valeur || '');
    };

    const addFormule = (e) => {
        e.preventDefault();
        if (!formuleNewName?.trim()) return;
        setFormules((prev) => [...prev, { libelleParametre: formuleNewName, valeur: formuleNewName }]);
        setFormuleNewName('');
        formuleInputRef.current?.focus();
    };

    const handleBeneficiaireChange = (value, option) => {
        const normalizedValue = normalizeOptionValue(value) || "";
        setSelectedBeneficiaireId(normalizedValue);

        if (!normalizedValue) {
            setBeneficiaire(null);
            setSelectedBeneficiaryOption(null);
            return;
        }

        const optionSource = Array.isArray(option) ? option[0] : option;
        setSelectedBeneficiaryOption(optionSource || null);
        const beneficiaryFromOption = optionSource?.beneficiary?.item;
        if (beneficiaryFromOption) {
            setBeneficiaire(beneficiaryFromOption);
            return;
        }

        const fallback = beneficiaryOptionLookup.get(normalizedValue);
        setBeneficiaire(fallback || null);
    };

    const selectedRepresentant = useMemo(() => {
        const isVictimeSelection =
            selectedBeneficiaryOption?.beneficiary?.type === "victimes" ||
            (!!beneficiaire?.representant && !!beneficiaire?.personne);
        if (!isVictimeSelection) {
            return null;
        }
        return selectedBeneficiaryOption?.beneficiary?.item?.representant || beneficiaire?.representant || null;
    }, [beneficiaire, selectedBeneficiaryOption]);

    const selectedRepresentantName = useMemo(() => formatRepresentantName(selectedRepresentant), [selectedRepresentant]);

    const handleSousEvChange = (value) => {
        setSousEvaluationsSelected(value);
    };

    const addItem = (e) => {
        e.preventDefault();

        if (!name?.trim()) {
            return; // Prevent adding empty or whitespace-only items
        }
        setMotifs((prevOptions) => [...prevOptions, { libelleParametre: name, valeur: name }]);

        setName(""); // Clear the input field

        inputRef.current?.focus(); // Focus the input directly
    };

    const handleNatureActeChange = (value) => {
        setNatureActe(value || undefined);
    };

    return (<>
        <Modal
            title={'Règlement de sinistre'}
            open={modalControl.open}
            confirmLoading={decaissementLoading}
            onCancel={() => {
                modalControl.setOpen(false)
                setBeneficiaire(null)
                setSelectedBeneficiaireId("")
                setNatureActe(undefined)
            }}
            okText="Valider"
            cancelText="Annuler"
            width={920}
            zIndex={2000}
            onOk={async () => {
                setDecaissementLoading(true);
                if (!beneficiaire) {
                    toast.error("Veuillez choisir un beneficiaire");
                    setDecaissementLoading(false);
                    return;
                }
                if (!motifReglement) {
                    toast.error("Veuillez choisir un motif de reglement");
                    setDecaissementLoading(false);
                    return;
                }
                if (!natureReglement?.length) {
                    toast.error("Veuillez choisir le type de reglement");
                    setDecaissementLoading(false);
                    return;
                }
                if (!montantNature?.length) {
                    toast.error("Veuillez renseigner au moins un montant de règlement");
                    setDecaissementLoading(false);
                    return;
                }
                if (natureReglement.includes("HONORAIRES") && !honoraire) {
                    toast.error("Veuillez choisir le taux de la retenue");
                    setDecaissementLoading(false);
                    return;
                }
                let results = [];
                montantNature?.forEach((mn) => {
                    let result = {
                        beneficiaire: beneficiaire.personne ? beneficiaire?.personne : beneficiaire,
                        prejudices: sousEvaluationsSelected,
                        montantGlobal: parseFloat(mn.montant || 0),
                        sinistre: sinistre,
                        motifReglement: motifReglement,
                        formuleDeFin: formuleClotureText?.trim() ? formuleClotureText.trim() : null,
                        taux: mn?.nature === "HONORAIRES" ? parseFloat(honoraire) : 0,
                        taxes: mn?.nature === "HONORAIRES" ? parseFloat(montantNature?.find((nature) => nature.nature === "HONORAIRES")?.montant || 0) * (parseFloat(honoraire) / 100) : 0,
                        expertise: mn?.nature === "HONORAIRES" ? true : false,
                        nature: mn.nature,
                        natureActe: natureActe,
                        representant: selectedRepresentantName || null,
                        encaissement: false
                    };
                    results.push(result)
                })

                try {
                    await saveDecaissements(results);
                    toast.success('Decaissement effectué !');
                    setDecaissementLoading(false);
                    modalControl.setOpen(false);
                    window.location.reload();
                    if (dossierCloture) {
                        handleClotureDossier();
                    }
                } catch (e) {
                    setDecaissementLoading(false);
                    toast.error(`Une erreur s'est produite !`);
                }
                //      modalControl.handleSubmit(result);
            }

            }
        >
            <Row gutter={[16, 16]}>

                <Col xs={24} md={12}>
                    <label
                        style={{
                            margin: "15px 0 5px 0",
                        }}
                    >
                        Nature du règlement *
                    </label>
                    <Select
                        mode="multiple"
                        name="natureReglement"
                        value={natureReglement}
                        placeholder="Sélectionner la nature du règlement"
                        style={{ width: "100%" }}
                        onChange={(value) => {
                            const selectedValues = Array.isArray(value) ? value : [];
                            const entries = selectedValues.map((nature) => {
                                const existing = montantNature?.find((item) => item.nature === nature);
                                return {
                                    nature,
                                    montant: existing ? existing.montant : 0,
                                };
                            });

                            setMontantNature(entries);
                            setNatureReglement(selectedValues);
                            const total = entries.reduce((acc, current) => acc + parseFloat(current.montant || 0), 0);
                            setMontantReglement(total);
                            if (!selectedValues.includes("HONORAIRES")) {
                                setHonoraire(0);
                            }
                        }}
                        options={[
                            {
                                label: "Sinistre en principal",
                                value: "SINISTRE EN PRINCIPAL"
                            },
                            {
                                label: "Frais accessoires",
                                value: "ACCESSOIRES"
                            },
                            {
                                label: "Honoraires",
                                value: "HONORAIRES"
                            },
                            {
                                label: "Provision",
                                value: "PROVISION"
                            }
                        ]} />

                </Col>
                <Col xs={24} md={12}>
                    <label
                        style={{
                            margin: "15px 0 5px 0",
                        }}
                    >
                        Sélectionner le bénéficiaire *
                    </label>
                    <Select
                        name="beneficiaire"
                        value={selectedBeneficiaireId || undefined}
                        placeholder="Sélectionner le bénéficiaire"
                        style={{ width: "100%" }}
                        onChange={handleBeneficiaireChange}
                        options={beneficiaryOptionGroups}
                        allowClear
                        disabled={beneficiarySelectDisabled}
                        notFoundContent="Aucun bénéficiaire disponible"
                    />
                </Col>
                {selectedRepresentant ? (
                    <Col span={24}>
                        <div
                            style={{
                                marginTop: 8,
                                padding: 12,
                                border: "1px solid #f0f0f0",
                                borderRadius: 8,
                                background: "#fafafa",
                            }}
                        >
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                Représentant de la victime
                            </div>
                            <div>{selectedRepresentantName || "Non renseigné"}</div>
                            {(selectedRepresentant?.contact?.length || selectedRepresentant?.adresse) ? (
                                <div style={{ color: "#555", marginTop: 4 }}>
                                    {selectedRepresentant?.contact?.[0] ? `Contact : ${selectedRepresentant.contact[0]}` : null}
                                    {selectedRepresentant?.contact?.[0] && selectedRepresentant?.adresse ? " — " : ""}
                                    {selectedRepresentant?.adresse ? `Adresse : ${selectedRepresentant.adresse}` : ""}
                                </div>
                            ) : null}
                        </div>
                    </Col>
                ) : null}


                <Col span={24}>
                    <label
                        style={{
                            margin: "15px 0 5px 0",
                        }}
                    >
                        Sélection le(s) préjudice(s)
                    </label>
                    <Select
                        name="sousEvaluations"
                        value={sousEvaluationsSelected}
                        placeholder="Sélectionner le(s) préjudice(s)"
                        style={{ width: "100%" }}
                        mode="tags"
                        onChange={handleSousEvChange}
                        options={[
                            {
                                label: "Préjudice matériel (dommages aux biens)",
                                value: "Préjudice matériel"
                            }, {
                                label: "Préjudice corporel (dommages physiques)",
                                value: "Préjudice corporel"
                            }, {
                                label: "Préjudice moral (souffrance psychologique)",
                                value: "Préjudice moral"
                            }, { label: "Préjudice économique (pertes financières)", value: "Préjudice économique" }
                        ]} />

                </Col>
                <Col span={24}>
                    <label
                        style={{
                            margin: "15px 0 5px 0",
                        }}
                    >
                        Motif de règlement *
                    </label>
                    <Spin spinning={motifsLoading}>
                        <Select
                            id="motifRevision"
                            name="motifRevision"
                            placeholder="Motif du règlement"
                            style={{ width: '100%', height: '50px' }}
                            dropdownRender={menu => (
                                <>
                                    {menu}
                                    <Divider style={{ margin: '8px 0' }} />
                                    <Space style={{ padding: '0 8px 4px' }}>
                                        <Input
                                            placeholder="Ajouter un motif"
                                            ref={inputRef}
                                            value={name}
                                            onChange={(e) => setName(e.target.value.toUpperCase())}
                                            onPressEnter={addItem}
                                        />
                                        <Button
                                            type="text"
                                            icon={<PlusOutlined />}
                                            onClick={addItem}
                                        >
                                            Ajouter un motif
                                        </Button>
                                    </Space>
                                </>
                            )}
                            options={motifs?.map((motif) => ({ label: motif?.libelleParametre, value: motif?.valeur })) || []}
                            onChange={onMotifSelect}
                            value={motifReglement}
                        />
                        <TextArea
                            rows={5}
                            value={textValue}
                            onChange={(e) => setTextValue(e.target.value)}
                            placeholder="Modifier le motif ici..."
                            style={{ marginTop: 10 }}
                        />
                    </Spin>
                </Col>
                {natureReglement?.map((reglement, index) => (
                    <Col
                        key={`${reglement}-${index}`}
                        xs={24}
                        md={(reglement === "HONORAIRES" && natureReglement?.length === 1) ? 12 : 24}
                    >
                        <label
                            style={{
                                margin: "15px 0 5px 0",
                            }}
                        >
                            Montant du règlement - {reglement} *
                        </label>

                        <AmountInputNumber
                            name="montantReglement"
                            value={montantNature?.find((nature) => nature.nature === reglement)?.montant || 0}
                            placeholder="Saisir le montant du reglement"
                            style={{ width: '100%' }}
                            onChange={(value) => {
                                const parsedValue = value === null || value === undefined || value === ""
                                    ? 0
                                    : parseFloat(value);

                                if (Number.isNaN(parsedValue)) {
                                    return;
                                }

                                const sapPlafond = parseFloat(sinistre?.sap ?? 0);

                                if (!Number.isNaN(sapPlafond) && parsedValue > sapPlafond) {
                                    toast.error(`Le montant du règlement ne peut pas dépasser le SAP (${sapPlafond})`);
                                    return;
                                }

                                if (parsedValue < 0) {
                                    toast.error(`Le montant du règlement ne peut pas être négatif`);
                                    return;
                                }

                                const updatedEntries = (montantNature || []).map((nature) =>
                                    nature.nature === reglement
                                        ? { ...nature, montant: parsedValue || 0 }
                                        : nature
                                );

                                const total = updatedEntries.reduce(
                                    (acc, current) => acc + parseFloat(current.montant || 0),
                                    0
                                );

                                if (!Number.isNaN(sapPlafond) && total > sapPlafond) {
                                    toast.error(`Le montant total du règlement ne peut pas dépasser le SAP (${sapPlafond})`);
                                    return;
                                }

                                if (total < 0) {
                                    toast.error(`Le montant total du règlement ne peut pas être négatif`);
                                    return;
                                }

                                setMontantNature(updatedEntries);
                                setMontantReglement(total);
                            }}
                        />
                    </Col>
                ))}
                {natureReglement?.includes("HONORAIRES") && (
                    <Col xs={24} md={natureReglement?.length === 1 ? 12 : 24}>
                        <label
                            style={{
                                margin: "15px 0 5px 0",
                            }}
                        >
                            Taux retenue *
                        </label>
                        <Select
                            name="tauxRetenue"
                            value={honoraire}
                            placeholder="Sélectionner le taux de retenue"
                            style={{ width: "100%" }}
                            onChange={setHonoraire}
                            options={[
                                {
                                    label: "3%",
                                    value: 3
                                },
                                {
                                    label: "5%",
                                    value: 5
                                },
                                {
                                    label: "20%",
                                    value: 20
                                },
                            ]} />
                    </Col>
                )}
                {honoraire ? (
                    <Col xs={24} md={12}>
                        <label
                            style={{
                                margin: "15px 0 5px 0",
                            }}
                        >
                            Montant retenue :{" "}
                            {formatAmount(
                                (parseFloat(honoraire) / 100) * parseFloat(montantNature?.find((nature) => nature.nature === "HONORAIRES")?.montant || 0),
                                { emptyValue: "0" }
                            )}
                        </label>
                    </Col>
                ) : null}
                <Col xs={24} md={(natureReglement?.includes("HONORAIRES") && natureReglement?.length === 1) ? 12 : 24}>
                    <label
                        style={{
                            margin: "15px 0 5px 0",
                        }}
                    >
                        Montant total
                    </label>
                    <AmountInputNumber
                        name="montantReglement"
                        value={montantReglement}
                        style={{ width: '100%' }}
                        disabled
                    />
                </Col>
                <Col span={24}>
                    <label style={{ margin: "15px 0 5px 0" }}>
                        Formule de clôture
                        {dossierCloture && (
                            <span style={{ color: "#dc2626", marginLeft: 6 }}>
                                * (règlement final pour ce bénéficiaire)
                            </span>
                        )}
                    </label>
                    <Spin spinning={formulesLoading}>
                        <Select
                            id="formuleCloture"
                            name="formuleCloture"
                            placeholder="Choisir ou saisir une formule de clôture"
                            style={{ width: '100%', height: '50px' }}
                            value={formuleCloture}
                            onChange={onFormuleSelect}
                            allowClear
                            dropdownRender={(menu) => (
                                <>
                                    {menu}
                                    <Divider style={{ margin: '8px 0' }} />
                                    <Space style={{ padding: '0 8px 4px' }}>
                                        <Input
                                            placeholder="Ajouter une formule"
                                            ref={formuleInputRef}
                                            value={formuleNewName}
                                            onChange={(e) => setFormuleNewName(e.target.value)}
                                            onPressEnter={addFormule}
                                        />
                                        <Button type="text" icon={<PlusOutlined />} onClick={addFormule}>
                                            Ajouter une formule
                                        </Button>
                                    </Space>
                                </>
                            )}
                            options={formules?.map((f) => ({
                                label: f?.libelleParametre,
                                value: f?.valeur,
                            })) || []}
                        />
                        <TextArea
                            rows={4}
                            value={formuleClotureText}
                            onChange={(e) => setFormuleClotureText(e.target.value)}
                            placeholder="Modifier la formule de clôture ici (sera imprimée sur l'ordre de dépense)..."
                            style={{ marginTop: 10 }}
                        />
                    </Spin>
                </Col>

                <Col span={24}>
                    <Switch checked={dossierCloture} checkedChildren={'Dossier clôturé'}
                        unCheckedChildren={'Dossier en instance'} disabled style={{
                            marginTop: '20px'
                        }} />
                </Col>
            </Row>
        </Modal >
    </>);
};

export default ReglementModal;
